import type { TBElement, TBText } from '@ttab/textbit'
import { Node, Text } from 'slate'
import { parse } from 'node-html-parser'
import { translate } from '@/shared/translate'
import { deserializeNode, serializeNode } from '@/shared/transformations/serialization'

type SlateNode = TBElement | TBText

// A photographer's name is not text to translate.
const UNTRANSLATED_TYPES = new Set(['core/image/byline', 'tt/visual/byline'])

function isInline(node: SlateNode): boolean {
  return Text.isText(node) || node.class === 'inline'
}

// Whole elements, so the service sees each sentence with its links and marks in place.
function collectTextElements(nodes: SlateNode[]): TBElement[] {
  const found: TBElement[] = []

  function walk(node: SlateNode): void {
    if (Text.isText(node) || UNTRANSLATED_TYPES.has(node.type)) {
      return
    }
    if (node.children.every(isInline)) {
      if (Node.string(node).trim() !== '') {
        found.push(node)
      }
      return
    }
    node.children.forEach(walk)
  }

  nodes.forEach(walk)
  return found
}

// A list item's one leaf holds its stored html verbatim, and is written back verbatim.
function holdsRawHtml(element: TBElement): boolean {
  return element.type.endsWith('/list-item')
}

function toHtml(element: TBElement): string {
  return holdsRawHtml(element) ? Node.string(element) : serializeNode(element)
}

function fromHtml(element: TBElement, html: string): void {
  if (holdsRawHtml(element)) {
    element.children = [{ text: html }]
    return
  }
  const value = deserializeNode(parse(html), {}, element.type)
  element.children = Array.isArray(value) ? value : [value]
}

type PrefValue = { enabled: boolean } | { words: { values: string[] } }

/**
 * Convert the comma-separated personal prefs string to the map format
 * expected by the Nynorsk API. Two entry shapes are supported:
 *   `rule_key`            -> { enabled: true }
 *   `rule_key=w1:w2:w3`   -> { words: { values: ['w1', 'w2', 'w3'] } }
 */
function parsePersonalPrefs(prefsString: string): Record<string, PrefValue> {
  const prefs: Record<string, PrefValue> = {}
  for (const raw of prefsString.split(',')) {
    const chunk = raw.trim()
    if (!chunk) continue

    const eq = chunk.indexOf('=')
    if (eq === -1) {
      prefs[chunk] = { enabled: true }
      continue
    }

    const key = chunk.slice(0, eq).trim()
    if (!key) continue

    const values = chunk.slice(eq + 1).split(':').map((v) => v.trim()).filter(Boolean)
    prefs[key] = { words: { values } }
  }
  return prefs
}

/**
 * Translate wire content from bokmål to nynorsk. Returns a cloned TBElement
 * tree with the same shape as the input, but with each text element's children
 * replaced by its translation. The original input is not mutated.
 */
export async function translateWireContent(
  wireContent: TBElement[],
  mode: 'standard' | 'personal',
  options: {
    ntbUrl: string
    accessToken: string
    personalPrefs?: string
  }
): Promise<TBElement[]> {
  const cloned = structuredClone(wireContent)
  const elements = collectTextElements(cloned)
  const texts = elements.map(toHtml)

  if (texts.length === 0) {
    return cloned
  }

  // `prefs_template` and `prefs` are alternatives in the NTB request - sending
  // both lets the named template override the user's explicit prefs. Personal
  // mode with no saved prefs falls back to the standard template.
  const personalPrefs = mode === 'personal' ? options.personalPrefs : undefined
  const result = await translate({
    texts: { values: texts },
    file_type: 'html',
    source_language: 'nb',
    target_language: 'nn',
    ...(personalPrefs
      ? { prefs: parsePersonalPrefs(personalPrefs) }
      : { prefs_template: 'standard' })
  }, {
    ntbUrl: options.ntbUrl,
    accessToken: options.accessToken
  })

  if (result.texts?.values?.length !== texts.length) {
    throw new Error(`Translation returned ${result.texts?.values?.length ?? 0} texts, expected ${texts.length}`)
  }

  const translated = result.texts.values
  elements.forEach((element, index) => fromHtml(element, translated[index]))
  return cloned
}
